import { useState, useRef } from "react";
import { PromoCode } from "@/types/admin";
import { format } from "date-fns";
import { Check, Loader2, Dices, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

import { useTranslation } from "react-i18next";

interface PromoCodeFormProps {
  initialData?: Partial<PromoCode>;
  onSubmit: (data: Partial<PromoCode>) => Promise<void>;
  loading: boolean;
}

export function PromoCodeForm({ initialData, onSubmit, loading }: PromoCodeFormProps) {
  const { t, i18n } = useTranslation();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    code: initialData?.code || "",
    description: initialData?.description || "",
    discount_type: (initialData?.discount_type || "percentage") as PromoCode['discount_type'],
    discount_value: initialData?.discount_value ?? "",
    max_uses: initialData?.max_uses || "",
    expires_at: initialData?.expires_at ? format(new Date(initialData.expires_at), 'yyyy-MM-dd') : "",
    is_active: initialData?.is_active ?? true,
    applicable_plans: initialData?.applicable_plans || [],
  });

  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      discount_value: formData.discount_value === "" ? undefined : Number(formData.discount_value),
      max_uses: formData.max_uses === "" ? null : Number(formData.max_uses),
      expires_at: formData.expires_at === "" ? null : new Date(formData.expires_at).toISOString(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Code Input */}
        <div className="space-y-2 relative z-10 w-full">
          <label className="text-sm font-black uppercase tracking-wider">{t('admin.promoCodes.form.code')}</label>
          <div className="flex gap-2 w-full">
            <input
              type="text"
              required
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className="flex-1 min-w-0 rounded-lg border-2 border-black bg-white px-4 py-2 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none"
              placeholder={t('admin.promoCodes.form.placeholders.code')}
            />
            <button
              type="button"
              onClick={generateCode}
              title={t('admin.promoCodes.form.generate')}
              className="flex-shrink-0 rounded-lg border-2 border-black bg-[#6FEDD6] px-3 py-2 font-black hover:shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px] transition-all"
            >
              <Dices className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Discount Type */}
        <div className="space-y-2 relative z-0">
          <label className="text-sm font-black uppercase tracking-wider">{t('admin.promoCodes.form.type')}</label>
          <select
            value={formData.discount_type}
            onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as PromoCode['discount_type'] })}
            className="w-full appearance-none rounded-lg border-2 border-black bg-white px-4 py-2 font-bold cursor-pointer hover:shadow-[4px_4px_0_#1A1A1A] focus:shadow-[4px_4px_0_#1A1A1A] focus:outline-none transition-shadow"
          >
            <option value="percentage">{t('admin.promoCodes.form.types.percentage')}</option>
            <option value="fixed">{t('admin.promoCodes.form.types.fixed')}</option>
            <option value="free_month">{t('admin.promoCodes.form.types.free_month')}</option>
          </select>
        </div>

        {/* Value */}
        <div className="space-y-2">
          <label className="text-sm font-black uppercase tracking-wider">{t('admin.promoCodes.form.value')}</label>
          <input
            type="number"
            disabled={formData.discount_type === 'free_month'}
            value={formData.discount_type === 'free_month' ? 100 : formData.discount_value}
            onChange={(e) => setFormData({ ...formData, discount_value: e.target.value === '' ? '' : Number(e.target.value) })}
            className="w-full rounded-lg border-2 border-black bg-white px-4 py-2 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none disabled:bg-gray-50"
            placeholder={t('admin.promoCodes.form.placeholders.value')}
          />
        </div>

        {/* Max Uses */}
        <div className="space-y-2">
          <label className="text-sm font-black uppercase tracking-wider">{t('admin.promoCodes.form.maxUses')}</label>
          <input
            type="number"
            value={formData.max_uses}
            onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
            className="w-full rounded-lg border-2 border-black bg-white px-4 py-2 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none"
            placeholder={t('admin.promoCodes.form.placeholders.maxUses')}
          />
        </div>

        {/* Expiry Date */}
        <div className="flex flex-col">
          <label className="text-sm font-black uppercase tracking-wider mb-2">{t('admin.promoCodes.form.expiresAt')}</label>
          <div 
            className="relative h-12 group cursor-pointer"
            onClick={() => {
              const input = dateInputRef.current;
              if (input) {
                const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
                if (pickerInput.showPicker) {
                  pickerInput.showPicker();
                } else {
                  input.click();
                }
              }
            }}
          >
            <input
              ref={dateInputRef}
              type="date"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              className="absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="flex h-full w-full items-center justify-between rounded-lg border-2 border-black bg-white px-4 font-bold shadow-none group-hover:shadow-[4px_4px_0_#000] transition-shadow">
              <span className={cn(formData.expires_at ? "text-black" : "text-gray-400")}>
                {formData.expires_at ? format(new Date(formData.expires_at), 'yyyy-MM-dd') : t('admin.promoCodes.form.placeholders.expiresAt')}
              </span>
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="flex flex-col">
          <label className="text-sm font-black uppercase tracking-wider mb-2">{t('admin.promoCodes.form.status')}</label>
          <div className="h-12 flex items-center gap-4">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
              className={cn(
                "relative w-20 h-12 rounded-full border-2 border-black transition-colors duration-200 flex-shrink-0",
                formData.is_active ? 'bg-[#FF6B9D]' : 'bg-gray-200'
              )}
            >
              <span
                className={cn(
                  "absolute top-[6px] left-[6px] w-[32px] h-[32px] bg-white border-2 border-black rounded-full transition-transform duration-200",
                  formData.is_active ? "translate-x-8" : "translate-x-0"
                )}
              />
            </button>
            <span className="font-bold">{formData.is_active ? t('admin.promoCodes.form.active') : t('admin.promoCodes.form.inactive')}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-black uppercase tracking-wider">{t('admin.promoCodes.form.description')}</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full rounded-lg border-2 border-black bg-white px-4 py-2 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none"
          rows={3}
          placeholder={t('admin.promoCodes.form.placeholders.description')}
        />
      </div>

      <div className="pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-black bg-[#FF6B9D] py-4 text-xl font-black shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Check className="h-6 w-6" />}
          {t('admin.promoCodes.form.saveConfig')}
        </button>
      </div>
    </form>
  );
}
